import React, {
  useEffect, useState, useCallback,
} from 'react';
import {
  FDBox,
  FDPrimaryActionButton,
  FDSecondaryActionButton,
  FDStack,
} from '@facet-wealth/design';
import { dataAttrs } from 'Helpers/Testing';
import { useSnackbarContext } from 'Context/SnackbarContext';
import {
  getUtmSource,
  useBuildCommonFQEventProperties,
} from 'Views/FreemiumEntry/HasRelationship/Content/Components/utils';
import useHomeContext from 'Context/HomeContext';
import useEventTrackingLogEvent, { categories, eventSubTypes, eventTypes } from 'EventTracking/useEventTrackingLogEvent';


const buildConfirmationUrl = (cpEventId, source, quizId) => {
  let url = `https://facet.com/confirmation-g/?meetingId=${cpEventId}`;

  if (source) {
    url += `&source=${source}`;
  }

  if (quizId) {
    url += `&quizId=${quizId}`;
  }

  return url;
};

const ChiliPiperConciergeComponent = ({
  onSchedulingSuccess = null,
  onSchedulingEvent = null,
  relationship = null,
  shouldAutoTriggerPhone = false,
  shouldShowMMButton = true,
  quizId,
  resource,
}) => {
  const { logStandardizedEvent } = useEventTrackingLogEvent();
  const { buildCommonFQEventProperties } = useBuildCommonFQEventProperties();
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [cpEventId, setCpEventId] = useState(null);
  const { displayErrorMessage } = useSnackbarContext();
  const { homeResource } = useHomeContext();

  let contactData = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    type: '',
    source: getUtmSource(homeResource),
    asset: '',
    journey: '',
    funnelPath: '',
  };

  if (relationship) {
    const relationshipUrl = relationship.link('related').url();
    const params = new URL(relationshipUrl).searchParams;
    contactData = {
      firstName: params.get('firstname') || '',
      lastName: params.get('lastname') || '',
      email: params.get('email') || '',
      phone: params.get('phone') || '',
      type: params.get('type') || '',
      source: getUtmSource(homeResource),
      journey: params.get('journey') || '',
      asset: params.get('asset') || '',
      funnelPath: params.get('funnelPath') || '',
    };
  }

  // Load ChiliPiper script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = relationship?.link('related')?.url();
    script.crossOrigin = 'anonymous';
    script.type = 'text/javascript';

    script.onload = () => setIsScriptLoaded(true);
    script.onerror = () => displayErrorMessage('Failed to load scheduler');

    document.head.appendChild(script);

    return () => {
      // Cleanup script on unmount
      document.head.removeChild(script);
    };
  }, [relationship]);

  // Set up message event listeners
  useEffect(() => {
    const handleMessage = (event) => {
      onSchedulingEvent?.(event.data);

      const { action } = event.data;

      if (action === 'booking-confirmed') {
        const { eventId } = event.data?.args;
        if (eventId) {
          setCpEventId(eventId);
          logStandardizedEvent(categories.facetQuiz, eventTypes.insight, eventSubTypes.impression, {
            eventProperties: {
              ...buildCommonFQEventProperties(resource),
              insightText: resource.attr('title'),
              insightId: resource.attr('insightId'),
              insightReferenceName: resource.attr('insightReferenceName'),
              action: 'booking confirmed',
              cpEventId,
            },
          });
        }
      }

      // Navigate to confirmation screen upon modal close after successful booking
      if (action === 'close' && cpEventId) {
        logStandardizedEvent(categories.facetQuiz, eventTypes.insight, eventSubTypes.clickedCTA, {
          eventProperties: {
            ...buildCommonFQEventProperties(resource),
            insightText: resource.attr('title'),
            insightId: resource.attr('insightId'),
            insightReferenceName: resource.attr('insightReferenceName'),
            action: 'exited confirmation modal',
            booked: true,
            cpEventId,
          },
        });
        window.location.href = buildConfirmationUrl(
          cpEventId,
          getUtmSource(homeResource),
          quizId,
        );
      }
      if (action === 'close' && !cpEventId) {
        logStandardizedEvent(categories.facetQuiz, eventTypes.insight, eventSubTypes.clickedCTA, {
          eventProperties: {
            ...buildCommonFQEventProperties(resource),
            insightText: resource.attr('title'),
            insightId: resource.attr('insightId'),
            insightReferenceName: resource.attr('insightReferenceName'),
            action: 'exited confirmation modal',
            booked: false,
          },
        });
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [onSchedulingEvent, cpEventId]);

  // Prepare email to handle spaces
  const prepareLeadEmail = useCallback((email) => {
    return email?.replace(/\s/g, '+') || '';
  }, []);

  function resetChiliPiper() {
    try {
      // This is the line missing from CP's code that causes the bug.
      window.ChiliPiper._marketingApi.popup.isPopupOpen = false;
    } catch (e) {
      // As a fallback, reload if the hack fails
      window.location.reload();
    }
  }

  // Centralized scheduling handler for both phone and video
  const handleScheduling = useCallback((type) => {
    if (!isScriptLoaded || !window.ChiliPiper) {
      return;
    }
    logStandardizedEvent(categories.facetQuiz, eventTypes.insight, eventSubTypes.clickedCTA, {
      eventProperties: {
        ...buildCommonFQEventProperties(resource),
        insightText: resource.attr('title'),
        insightId: resource.attr('insightId'),
        insightReferenceName: resource.attr('insightReferenceName'),
        action: 'clicked schedule',
        meetingType: type,
      },
    });

    const email = prepareLeadEmail(contactData.email);

    const chiliOptions = {
      trigger: 'ThirdPartyForm',
      lead: {
        ...contactData,
        email,
        queueType: contactData.type,
      },
      event: {
        cp_Journey__c: contactData.journey,
        cp_Type__c: contactData.type,
        cp_Asset__c: contactData.asset,
      },
      onSuccess: () => {
        onSchedulingSuccess?.(type);
      },
      onClose: resetChiliPiper,
    };

    const routerName = type === 'phone' ? 'schedule-intro---phone' : 'schedule-intro---zoom';

    const { hostname } = new URL(relationship?.link('related')?.url());
    const subdomain = hostname.split('.')[0];
    window.ChiliPiper.submit(subdomain, routerName, chiliOptions);
  }, [isScriptLoaded, onSchedulingSuccess, contactData, prepareLeadEmail]);

  // Auto-trigger phone scheduling when prop is set and script is loaded
  useEffect(() => {
    if (shouldAutoTriggerPhone && isScriptLoaded) {
      handleScheduling('phone');
    }
  }, [shouldAutoTriggerPhone, isScriptLoaded, handleScheduling]);

  return (
    <FDBox flexGrow={1} width="100%">

      <FDStack direction="column" spacing={2}>
        <FDPrimaryActionButton
          onClick={() => handleScheduling('phone')}
          loading={!isScriptLoaded}
          label="Phone introductory call"
          isFullWidth
          {...dataAttrs('chili-piper-phone-schedule-button')}
        />

        {shouldShowMMButton && (
          <FDSecondaryActionButton
            onClick={() => handleScheduling('video')}
            loading={!isScriptLoaded}
            label="Video introductory call"
            isFullWidth
            {...dataAttrs('chili-piper-video-schedule-button')}
          />
        )}
      </FDStack>
    </FDBox>
  );
};

export default ChiliPiperConciergeComponent;
